import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

import {
  eInvoiceApi,
  eInvoiceQueryKeys,
  type EInvoiceConfigInput,
  type EInvoiceConfigRow,
} from "../../shared/api/platformBilling";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Tài khoản hoá đơn điện tử CỦA ADDY (#18990).
 *
 * <p>Đây là chiều Addy bán cho phòng khám, không phải chiều phòng khám bán cho bệnh nhân — hai
 * chiều dùng hai tài khoản nhà cung cấp khác nhau và không dùng lại được của nhau.
 *
 * <p><b>Vì sao là màn hình chứ không phải biến môi trường.</b> Ký hiệu hoá đơn đổi theo năm, mẫu số
 * đổi theo hợp đồng, client secret bị xoay vòng — mỗi thứ là một sự kiện nghiệp vụ chứ không phải
 * một lần deploy. Để trong cấu hình nghĩa là kế toán phải mở ticket và chờ kỹ sư.
 *
 * <p>Client secret <b>không bao giờ</b> hiện lại: nó được mã hoá khi lưu và API không có ô nào chở
 * nổi nó ra. Ô để trống nghĩa là giữ nguyên.
 */
export function PlatformEInvoicePage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: eInvoiceQueryKeys.config(), queryFn: () => eInvoiceApi.config() });
  const rows = q.data ?? [];
  const active = rows.find((r) => r.status === "ACTIVE") ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-foreground">Hoá đơn điện tử</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Tài khoản nhà cung cấp mà <b>Addy</b> dùng để xuất hoá đơn GTGT cho phòng khám. Khác với
          tài khoản hoá đơn của từng phòng khám — hai chiều bán hàng khác nhau.
        </p>
      </div>

      {q.isError && <p className="text-sm text-destructive">{apiError(q.error).detail}</p>}
      {q.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}

      {!q.isLoading && !active && (
        <div className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <span>
            <b>Chưa có tài khoản nào đang hoạt động.</b> Hàng đợi hoá đơn vẫn ghi nhận đầy đủ mọi
            khoản đã thu — không khoản nào mất dấu — nhưng hệ thống chưa tự phát hành được.
          </span>
        </div>
      )}
      {active && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/5 p-3 text-sm">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <span>
            Đang phát hành qua <b>{active.provider}</b>, ký hiệu <b>{active.serial}</b>, mẫu số{" "}
            <b>{active.templateName}</b>.
          </span>
        </div>
      )}

      {rows.map((row) => (
        <ConfigCard key={row.id} row={row} onChanged={() => void qc.invalidateQueries({ queryKey: eInvoiceQueryKeys.config() })} />
      ))}

      <ConfigCard
        row={null}
        onChanged={() => void qc.invalidateQueries({ queryKey: eInvoiceQueryKeys.config() })}
      />
    </div>
  );
}

const FIELDS: { key: keyof EInvoiceConfigInput; label: string; hint?: string; numeric?: boolean }[] =
  [
    { key: "baseUrl", label: "Địa chỉ máy chủ", hint: "Sandbox và production là hai địa chỉ khác nhau" },
    { key: "clientId", label: "Client ID" },
    { key: "clientSecret", label: "Client secret", hint: "Để trống = giữ nguyên. Không bao giờ hiện lại." },
    { key: "scope", label: "Scope" },
    { key: "idPartner", label: "ID Partner", numeric: true },
    { key: "invoiceType", label: "Loại hoá đơn", numeric: true },
    { key: "templateName", label: "Mẫu số", hint: "Ví dụ 1/001" },
    { key: "serial", label: "Ký hiệu", hint: "Ví dụ C26TAA" },
  ];

function ConfigCard({ row, onChanged }: { row: EInvoiceConfigRow | null; onChanged: () => void }) {
  const [form, setForm] = useState<EInvoiceConfigInput>({});
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => eInvoiceApi.save(row?.id ?? null, form),
    onSuccess: () => {
      setError(null);
      setForm({});
      onChanged();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const activate = useMutation({
    mutationFn: () => eInvoiceApi.activate(row!.id),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const current = (key: keyof EInvoiceConfigInput): string => {
    const typed = form[key];
    if (typed !== undefined && typed !== null) return String(typed);
    if (!row) return "";
    if (key === "clientSecret") return "";
    const stored = row[key as keyof EInvoiceConfigRow];
    return stored === null || stored === undefined ? "" : String(stored);
  };

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          {row ? `${row.provider} — ${row.status}` : "Khai tài khoản mới (PA Vietnam)"}
        </h2>
        {row && row.status !== "ACTIVE" && (
          <Button
            size="sm"
            className="h-8"
            disabled={activate.isPending}
            onClick={() => activate.mutate()}
          >
            {activate.isPending ? "Đang kích hoạt…" : "Đưa vào sử dụng"}
          </Button>
        )}
      </div>

      {row && row.missing.length > 0 && (
        <p className="text-xs text-amber-700">
          {/* Danh sách do chính lớp nhà cung cấp trả về, nên nó không lệch với thứ PA thực sự đòi. */}
          Còn thiếu: {row.missing.join(", ")}
        </p>
      )}
      {row && !row.hasClientSecret && (
        <p className="text-xs text-amber-700">Chưa khai client secret.</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FIELDS.map((f) => (
          <label key={f.key} className="space-y-1">
            <span className="text-xs font-medium text-foreground">{f.label}</span>
            <Input
              className="h-8"
              type={f.key === "clientSecret" ? "password" : f.numeric ? "number" : "text"}
              autoComplete={f.key === "clientSecret" ? "new-password" : "off"}
              value={current(f.key)}
              placeholder={f.key === "clientSecret" && row?.hasClientSecret ? "đã khai" : ""}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  [f.key]: f.numeric
                    ? e.target.value === ""
                      ? null
                      : Number(e.target.value)
                    : e.target.value,
                }))
              }
            />
            {f.hint && <span className="block text-[11px] text-muted-foreground">{f.hint}</span>}
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={row ? "outline" : "default"}
          className="h-8"
          disabled={save.isPending || Object.keys(form).length === 0}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Đang lưu…" : row ? "Lưu thay đổi" : "Khai tài khoản"}
        </Button>
        {error && <span className="text-xs text-destructive">{error}</span>}
      </div>
    </div>
  );
}
